<template>
  <div v-if="!auth.ready" class="flex h-full items-center justify-center">
    <AltSpinner size="lg" />
  </div>

  <template v-else>
    <!-- Pages publiques (connexion, retour de Microsoft) : sans le menu. -->
    <RouterView v-if="isPublic" />

    <AltAppLayout
      v-else
      :items="nav"
      brand="its"
      title="Borne technique"
      :page-title="pageTitle"
      :active-path="cheminActif"
      :link-component="RouterLink"
      @navigate="ouvrirLienExterne"
    >
      <!--
        ⚠ CE SLOT DOIT TOUJOURS RENDRE UN ÉLÉMENT, même replié : un `v-if` seul
        ne laisse qu'un commentaire, que Vue tient pour « non fourni » — et
        `AltSidebar` retombe sur son logo par défaut, écrasé dans la barre
        repliée. Même piège, même parade que dans Plans d'actions.
      -->
      <template #sidebar-header="{ collapsed }">
        <span v-if="!collapsed" class="truncate font-semibold text-white">Borne technique</span>
        <span v-else class="sr-only">Borne technique</span>
      </template>

      <template #sidebar-icon="{ item }">
        <AppIcon :nom="pictoDe(item)" :taille="17" />
      </template>

      <template #topbar-actions>
        <div class="flex items-center gap-3">
          <AltBadge v-if="environnement" variant="warning" size="sm">{{ environnement }}</AltBadge>
          <!-- Seule l'équipe prend des demandes en charge : la cloche est pour elle. -->
          <ClocheMessages />
          <ProfileMenu data-tour="profil" />
        </div>
      </template>

      <RouterView v-slot="{ Component, route: r }">
        <Transition name="fade" mode="out-in">
          <!-- La génération change à chaque bascule de monde (démo, rôle) : l'écran se remonte et recharge. -->
          <component :is="Component" :key="`${r.path}#${decouverte.generation}`" />
        </Transition>
      </RouterView>
    </AltAppLayout>

    <NouvelleDemandeModal />
    <ConfirmerHote />
    <AltToastContainer position="bottom-right" />
  </template>
  <!-- Mode démo et visite guidée : au-dessus de tout, y compris hors coquille. -->
  <BandeauDemo />
  <VisiteGuidee />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { LIENS_AIDE } from '@/lib/liens-aide';
import { createToastStore } from 'altoneo-charting';
import {
  canAdminister,
  canBill,
  canHandleRequests,
  canViewClients,
  peutSaisirTemps,
  peutVoirTableauDeBord,
  type Outil,
  type OutilLienDTO,
} from '@its/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { config } from '@/lib/config';
import AppIcon from '@/components/AppIcon.vue';
import ProfileMenu from '@/components/ProfileMenu.vue';
import ClocheMessages from '@/components/ClocheMessages.vue';
import ConfirmerHote from '@/components/ConfirmerHote.vue';
import NouvelleDemandeModal from '@/components/NouvelleDemandeModal.vue';
import BandeauDemo from '@/components/decouverte/BandeauDemo.vue';
import VisiteGuidee from '@/components/decouverte/VisiteGuidee.vue';
import { useDecouverteStore } from '@/stores/decouverte';

// Obligatoire avant tout `useToast()` : c'est lui qui crée le magasin des toasts.
createToastStore();

const auth = useAuthStore();
const route = useRoute();
const decouverte = useDecouverteStore();

const isPublic = computed(() => route.meta.public === true);

/**
 * L'entrée de menu ALLUMÉE. `AltSidebar` allume toute entrée dont le chemin
 * préfixe l'adresse — d'où des chemins qui ne se préfixent pas (/file,
 * /mes-demandes). Une fiche « /demandes/:id » appartient à la file pour
 * l'équipe, à « Mes demandes » pour le demandeur.
 */
const cheminActif = computed(() => {
  if (route.path.startsWith('/demandes/')) return canHandleRequests(auth.roles) ? '/file' : '/mes-demandes';
  if (route.path.startsWith('/clients/')) return '/clients';
  return route.path;
});
const pageTitle = computed(() => (route.meta.title as string | undefined) ?? '');

// Le bandeau d'environnement évite de saisir en recette ce qu'on voulait en
// production, et l'inverse. Rien en production : c'est l'état normal.
const environnement = computed(() =>
  config.ENVIRONNEMENT && !['prod', 'production'].includes(config.ENVIRONNEMENT)
    ? config.ENVIRONNEMENT.toUpperCase()
    : '',
);

type NomPicto = InstanceType<typeof AppIcon>['$props']['nom'];

/** Indexé par la ROUTE, pas par le libellé : le libellé se retouche, le chemin non. */
const PICTOS_NAV: Record<string, NomPicto> = {
  '/tableau-de-bord': 'maison',
  '/mes-demandes': 'liste',
  '/file': 'boite',
  '/periodiques': 'calendrier',
  '/clients': 'dossier',
  '/temps': 'horloge',
  '/interventions': 'jauge',
  '/prefacturation': 'document',
  '/notes-facturation': 'registre',
  '/administration/comptes': 'membres',
  '/administration/types-intervention': 'reglages',
  '/decouverte': 'boussole',
};
const PICTOS_AIDE: Record<string, NomPicto> = { 'aide-flipbook': 'livre', 'aide-sav': 'mallette', 'aide-tarifs': 'registre', 'aide-cgv': 'document' };
const PICTOS_OUTILS: Record<Outil, NomPicto> = { borne_rh: 'personne', gestion_actifs: 'conteneur', borne_commerciale: 'cible' };
const cleOutil = (o: Outil): string => `outil-${o}`;
const pictoDe = (item: { to?: string; key?: string }): NomPicto => {
  const outil = outils.value.find((o) => cleOutil(o.outil) === item.key);
  return PICTOS_NAV[item.to ?? ''] ?? PICTOS_AIDE[item.key ?? ''] ?? (outil ? PICTOS_OUTILS[outil.outil] : 'cercle');
};

/**
 * MES AUTRES OUTILS (menu latéral) : les liens vers la borne RH, la gestion des
 * actifs et la borne commerciale, sans passer par le menu du profil. L'API dit
 * lesquels ce compte peut ouvrir (`outilsAccessibles`, @its/shared) ; relus à
 * chaque changement de compte (connexion, démo).
 */
const outils = ref<OutilLienDTO[]>([]);
watch(
  () => auth.user?.userId,
  (id) => {
    outils.value = [];
    if (id) api.get<OutilLienDTO[]>('/me/outils').then((l) => (outils.value = l), () => undefined);
  },
  { immediate: true },
);

/**
 * Les documents IT Solutions du menu « Aide » et les autres outils du groupe
 * s'ouvrent dans un nouvel onglet : la borne reste où l'on était. `AltSidebar` rend une entrée sans `to` en bouton
 * et émet `navigate` ; on n'ouvre que les adresses de `LIENS_AIDE` et de `/me/outils`.
 */
function ouvrirLienExterne(item: { key?: string }): void {
  const lien = LIENS_AIDE.find((l) => l.key === item.key) ?? outils.value.find((o) => cleOutil(o.outil) === item.key);
  if (lien) window.open(lien.url, '_blank', 'noopener');
}

/**
 * Le menu ne montre que ce que le compte peut ouvrir — par confort : c'est
 * l'API qui refuse. Les règles viennent de `@its/shared`, jamais recopiées ici.
 */
const nav = computed(() => {
  const roles = auth.roles;
  return [
    { heading: 'Mon espace' },
    ...(peutVoirTableauDeBord(auth.user?.kind) ? [{ to: '/tableau-de-bord', label: 'Tableau de bord' }] : []),
    // Pas d'entrée « Nouvelle demande » : le bouton de « Mes demandes » (et de la
    // file) ouvre la modale ; deux chemins vers le même geste encombraient le menu.
    { to: '/mes-demandes', label: 'Mes demandes' },
    ...(canViewClients(roles) ? [{ heading: 'Assistance' }] : []),
    ...(canHandleRequests(roles)
      ? [
          { to: '/file', label: 'File des demandes' },
          { to: '/periodiques', label: 'Demandes périodiques' },
        ]
      : []),
    ...(canViewClients(roles) ? [{ to: '/clients', label: 'Clients' }] : []),
    ...(peutSaisirTemps(roles) ? [{ heading: 'Temps' }, { to: '/temps', label: 'Ma feuille de temps' }] : []),
    ...(canViewClients(roles) ? [{ to: '/interventions', label: 'Interventions' }] : []),
    ...(canBill(roles) ? [
          { heading: 'Facturation' },
          { to: '/prefacturation', label: 'Pré-facturation' },
          { to: '/notes-facturation', label: 'Notes de facturation' },
        ] : []),
    ...(canAdminister(roles)
      ? [
          { heading: 'Administration' },
          { to: '/administration/comptes', label: 'Comptes' },
          { to: '/administration/types-intervention', label: 'Types d’intervention' },
        ]
      : []),
    ...(outils.value.length
      ? [{ heading: 'Mes autres outils' }, ...outils.value.map((o) => ({ key: cleOutil(o.outil), label: o.libelle }))]
      : []),
    { heading: 'Aide' },
    { to: '/decouverte', label: 'Visite guidée' },
    ...LIENS_AIDE.map((l) => ({ key: l.key, label: l.label })),
  ];
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
